import React, { lazy, Suspense } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

// Eager imports — small + needed on every route boot.
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import HrefLangTags from "@/components/HrefLangTags";
import BackToTop from "@/components/BackToTop";
import AiAssistant from "@/components/AiAssistant";
import AuthCallback from "@/pages/AuthCallback";

// Lazy-loaded routes — each renders only when navigated to.
// Cuts the main bundle from ~936 KB to ~150-200 KB on first paint.
const Landing = lazy(() => import("@/pages/Landing"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Listings = lazy(() => import("@/pages/Listings"));
const NewListing = lazy(() => import("@/pages/NewListing"));
const ListingDetail = lazy(() => import("@/pages/ListingDetail"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const ConversationThread = lazy(() => import("@/pages/ConversationThread"));
const Settings = lazy(() => import("@/pages/Settings"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Legal = lazy(() => import("@/pages/Legal"));
const Contact = lazy(() => import("@/pages/Contact"));
const Mieterselbstauskunft = lazy(() => import("@/pages/Mieterselbstauskunft"));
const LoyerVerite = lazy(() => import("@/pages/LoyerVerite"));
const Mietpreisbremse = lazy(() => import("@/pages/Mietpreisbremse"));
const PrecioAlquiler = lazy(() => import("@/pages/PrecioAlquiler"));
const UkDepositProtection = lazy(() => import("@/pages/UkDepositProtection"));
const CanoneConcordato = lazy(() => import("@/pages/CanoneConcordato"));
const Anfechtung = lazy(() => import("@/pages/Anfechtung"));
const NlPuntensysteem = lazy(() => import("@/pages/NlPuntensysteem"));
const BeLoyerReference = lazy(() => import("@/pages/BeLoyerReference"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Demo = lazy(() => import("@/pages/Demo"));

function RouteFallback() {
  // Subtle full-screen brand-tinted skeleton while a route chunk loads.
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white"
      data-testid="route-fallback"
    >
      <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-[#4F7CFF] animate-spin" />
    </div>
  );
}

/**
 * Scroll to top on every path change — React Router v6 doesn't do it by
 * default. Fixes the "footer links land you at the bottom of the next page"
 * bug reported by users. Respects in-page hash navigation (#faq etc.)
 * so anchor links still work.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) return; // let the browser handle #anchor scrolling
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function Router() {
  const location = useLocation();
  // CRITICAL: detect session_id in URL fragment SYNCHRONOUSLY (before useEffect),
  // otherwise AuthProvider's /me check will race with the session exchange.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/listings" element={<ProtectedRoute><Listings /></ProtectedRoute>} />
        <Route path="/listings/new" element={<ProtectedRoute><NewListing /></ProtectedRoute>} />
        <Route path="/listings/:id" element={<ProtectedRoute><ListingDetail /></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
        <Route path="/inbox/:id" element={<ProtectedRoute><ConversationThread /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/mieterselbstauskunft" element={<Mieterselbstauskunft />} />
        <Route path="/loyer-verite" element={<LoyerVerite />} />
        <Route path="/loyer-verite/:city" element={<LoyerVerite />} />
        <Route path="/mietpreisbremse" element={<Mietpreisbremse />} />
        <Route path="/mietpreisbremse/:stadt" element={<Mietpreisbremse />} />
        <Route path="/precio-alquiler" element={<PrecioAlquiler />} />
        <Route path="/precio-alquiler/:ciudad" element={<PrecioAlquiler />} />
        <Route path="/uk-deposit-protection" element={<UkDepositProtection />} />
        <Route path="/uk-deposit-protection/:city" element={<UkDepositProtection />} />
        <Route path="/canone-concordato" element={<CanoneConcordato />} />
        <Route path="/canone-concordato/:citta" element={<CanoneConcordato />} />
        <Route path="/anfechtung-anfangsmiete" element={<Anfechtung />} />
        <Route path="/anfechtung-anfangsmiete/:stadt" element={<Anfechtung />} />
        <Route path="/nl-puntensysteem" element={<NlPuntensysteem />} />
        <Route path="/nl-puntensysteem/:stad" element={<NlPuntensysteem />} />
        <Route path="/be-loyer-reference" element={<BeLoyerReference />} />
        <Route path="/be-loyer-reference/:commune" element={<BeLoyerReference />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <HrefLangTags />
            <Router />
            <BackToTop />
            <AiAssistant />
            <CookieBanner />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
